import { ColorSwatch } from "@/components/dialogs/AddColumnDialog";
import { Button, IconButton } from "@/components/ui/Button";
import { dialogInputStyle } from "@/components/ui/Dialog";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { StatusOption } from "@/lib/types";

interface Props {
  options: StatusOption[];
  /** When the underlying column is required, block archiving the last active
   *  option — the submit form would then demand a field with nothing to pick. */
  required?: boolean;
  onChange: (idx: number, patch: Partial<StatusOption>) => void;
  onAdd: () => void;
}

/**
 * Editable list of a status column's options: recolor + rename + archive/restore
 * per row, plus an add button. Archive (not hard delete) keeps the id in the
 * array so cells that already used the option still resolve. Shared by the
 * single-column {@link StatusOptionsDialog} and the ticket settings panel.
 */
export function StatusOptionRows({ options, required, onChange, onAdd }: Props) {
  const activeCount = options.filter((o) => !o.archived).length;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {options.map((o, i) => (
        <div
          key={o.id}
          style={{ display: "flex", gap: 6, alignItems: "center", opacity: o.archived ? 0.5 : 1 }}
        >
          <ColorSwatch value={o.color} onChange={(col) => onChange(i, { color: col })} />
          <input
            value={o.label}
            onChange={(e) => onChange(i, { label: e.target.value })}
            placeholder={t.ticketSettingsOptionPlaceholder}
            style={{ ...dialogInputStyle, flex: 1 }}
          />
          <IconButton
            icon={o.archived ? "undo" : "archive"}
            size={28}
            title={o.archived ? t.unarchiveOption : t.archiveOption}
            onClick={() => {
              if (!o.archived && required && activeCount <= 1) {
                showToast(t.ticketSettingsRequiredLastOption, "error");
                return;
              }
              onChange(i, { archived: !o.archived });
            }}
          />
        </div>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        icon="plus"
        onClick={onAdd}
        style={{ alignSelf: "flex-start", marginTop: 4 }}
      >
        {t.addOption}
      </Button>
    </div>
  );
}
